import { Fragment } from 'react'

import { Skeleton } from '#/shared/ui/skeleton'

/**
 * Loading states for the campaign screens.
 *
 * Each one mirrors the geometry of what replaces it — same wrappers, same
 * paddings, same line heights — so the data landing swaps grey for text
 * without moving anything. They are `aria-hidden`: a screen reader gains
 * nothing from a description of placeholder boxes.
 */

/** How many tiles the strip holds today. Widths are `flex-1`, so the box keeps
 *  its height even if the service starts sending the fifth. */
const SUMMARY_TILE_COUNT = 4

export function CampaignSummarySkeleton() {
  return (
    <div
      aria-hidden="true"
      className="flex items-center overflow-hidden rounded-xl border border-gray-200 bg-white py-4 max-md:flex-wrap max-md:gap-y-4"
    >
      {Array.from({ length: SUMMARY_TILE_COUNT }, (_, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <span className="h-10 w-px shrink-0 bg-gray-200 max-md:hidden" />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1 px-5 max-md:basis-1/2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6.5 w-28" />
            <Skeleton className="h-4 w-32" />
          </div>
        </Fragment>
      ))}
    </div>
  )
}

/** The header card plus the shapes of the two blocks that open every tab. */
export function CampaignDetailSkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-4">
      <Skeleton className="h-4 w-24" />
      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4 px-4 py-3.5">
          <div className="flex min-w-0 flex-col gap-1.5">
            <Skeleton className="h-7 w-56" />
            <Skeleton className="h-4 w-full max-w-140" />
          </div>
          <Skeleton className="h-7 w-64" />
        </div>
        <div className="px-4 pb-3">
          <Skeleton className="h-8 w-80" />
        </div>
      </section>
      <Skeleton className="h-30 w-full rounded-xl" />
      <div className="flex flex-col gap-4 xl:flex-row">
        <Skeleton className="h-80 min-w-0 flex-1 rounded-xl" />
        <Skeleton className="h-80 w-full shrink-0 rounded-xl xl:w-90" />
      </div>
    </div>
  )
}

/** The four stacked cards of the sequence rail. */
export function CampaignSequenceRailSkeleton() {
  const heights = ['h-64', 'h-30', 'h-44', 'h-40']
  return (
    <div aria-hidden="true" className="flex flex-col gap-4">
      {heights.map((height) => (
        <Skeleton key={height} className={`${height} w-full rounded-xl`} />
      ))}
    </div>
  )
}

/** Step cards, with the wait connector's gap between them. */
export function CampaignStepsSkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col">
      {[0, 1].map((index) => (
        <Fragment key={index}>
          {index > 0 && <div className="h-14" />}
          <Skeleton className="h-56 w-full rounded-xl" />
        </Fragment>
      ))}
    </div>
  )
}

const ACTIVITY_ROW_COUNT = 6

/** Feed rows, on the same 1px rule the real ones sit on. */
export function CampaignActivitySkeleton() {
  return (
    <ul aria-hidden="true">
      {Array.from({ length: ACTIVITY_ROW_COUNT }, (_, index) => (
        <li
          key={index}
          className="flex flex-wrap items-start gap-x-3.5 gap-y-1.5 border-b border-gray-200 px-4 py-3 last:border-b-0"
        >
          <Skeleton className="h-4 w-27.5 shrink-0" />
          <div className="flex w-37.5 shrink-0 items-center gap-2">
            <Skeleton className="size-5.5 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="flex min-w-full flex-1 flex-col gap-1 sm:min-w-0">
            <Skeleton className="h-5 w-56 max-w-full" />
            <Skeleton className="h-4 w-72 max-w-full" />
          </div>
        </li>
      ))}
    </ul>
  )
}
